import type { ReactElement } from "react";
import type { LucideIcon } from "lucide-react";

import { Sparkles, Printer, Gift, Truck, Heart, Tag } from "lucide-react";

/**
 * Feature configuration
 */
interface Feature {
  icon: LucideIcon;
  text: string;
  color: string;
}

const FEATURES: Feature[] = [
  {
    icon: Printer,
    text: "Professional quality hardcover & softcover options",
    color: "var(--st-primary)",
  },
  {
    icon: Truck,
    text: "Ships directly to your door",
    color: "#10B981",
  },
  {
    icon: Gift,
    text: "Perfect gift for grandparents, birthdays, holidays",
    color: "#8B5CF6",
  },
  {
    icon: Tag,
    text: "Subscriber discounts available",
    color: "#F59E0B",
  },
];

/**
 * PrintComingSoonSection Component
 *
 * Promotional section for upcoming print-on-demand feature.
 */
const PrintComingSoonSection = (): ReactElement => {
  return (
    <section
      className="py-16 px-4 print-section-bg"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center gap-10">
          {/* Left: Text Content */}
          <div className="flex-1 text-center lg:text-left">
            {/* Coming Soon Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4"
              style={{ background: "var(--st-border-light)" }}
            >
              <Sparkles className="w-4 h-4" style={{ color: "var(--st-primary)" }} />
              <span
                className="text-xs font-bold tracking-wide"
                style={{
                  color: "var(--st-primary)",
                  fontFamily: "Quicksand, sans-serif",
                }}
              >
                COMING SOON
              </span>
            </div>

            {/* Headline */}
            <h2
              className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              Turn Digital Adventures into{" "}
              <span style={{ color: "var(--st-primary)" }}>Treasured Keepsakes</span>
            </h2>

            {/* Subtitle */}
            <p
              className="text-base mb-6 max-w-xl mx-auto lg:mx-0"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-body)" }}
            >
              Premium print-on-demand books of your child's stories - coming
              soon!
            </p>

            {/* Features List */}
            <div className="space-y-3 mb-6">
              {FEATURES.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div
                    key={index}
                    className="flex items-center gap-3 text-left justify-center lg:justify-start"
                  >
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                      style={{ background: `${feature.color}15` }}
                    >
                      <Icon
                        className="w-4 h-4"
                        style={{ color: feature.color }}
                      />
                    </div>
                    <span
                      className="text-sm font-medium"
                      style={{
                        fontFamily: "Quicksand, sans-serif",
                        color: "var(--st-text-heading)",
                      }}
                    >
                      {feature.text}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Book Mockup Visual */}
          <div className="flex-1 flex justify-center">
            <div className="relative">
              {/* Book mockup placeholder */}
              <div
                className="w-64 h-80 md:w-72 md:h-96 rounded-xl relative overflow-hidden"
                style={{
                  background:
                    "linear-gradient(145deg, var(--st-card-bg) 0%, var(--st-bg-page) 100%)",
                  boxShadow:
                    "0 20px 40px rgba(var(--st-shadow-color), 0.15), 0 10px 20px rgba(var(--st-shadow-color), 0.1)",
                  border: "1px solid var(--st-border-light)",
                }}
              >
                {/* Book spine effect */}
                <div
                  className="absolute left-0 top-0 bottom-0 w-4"
                  style={{
                    background:
                      "linear-gradient(90deg, var(--st-border-medium) 0%, var(--st-border-light) 100%)",
                    borderRight: "1px solid var(--st-border-medium)",
                  }}
                />

                {/* Book content area */}
                <div className="absolute inset-0 left-4 flex flex-col items-center justify-center p-6 text-center">
                  {/* Placeholder book cover design */}
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-4"
                    style={{
                      background:
                        "linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)",
                      boxShadow: "0 6px 20px rgba(var(--st-glow-color), 0.3)",
                    }}
                  >
                    <Printer className="w-8 h-8 text-white" />
                  </div>

                  <h3
                    className="text-lg font-bold mb-2"
                    style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
                  >
                    Your Story
                  </h3>
                  <p
                    className="text-xs mb-4"
                    style={{
                      fontFamily: "Quicksand, sans-serif",
                      color: "var(--st-text-muted)",
                    }}
                  >
                    Professionally Printed
                  </p>

                  {/* Decorative lines representing pages */}
                  <div className="w-full space-y-2">
                    <div
                      className="h-1.5 rounded-full mx-auto"
                      style={{ background: "var(--st-border-light)", width: "80%" }}
                    />
                    <div
                      className="h-1.5 rounded-full mx-auto"
                      style={{ background: "var(--st-border-light)", width: "60%" }}
                    />
                    <div
                      className="h-1.5 rounded-full mx-auto"
                      style={{ background: "var(--st-border-light)", width: "70%" }}
                    />
                  </div>
                </div>

                {/* Page edges effect */}
                <div
                  className="absolute right-0 top-2 bottom-2 w-1"
                  style={{
                    background:
                      "linear-gradient(180deg, var(--st-bg-muted) 0%, var(--st-border-light) 50%, var(--st-bg-muted) 100%)",
                  }}
                />
              </div>

              {/* Floating heart decoration */}
              <div
                className="absolute -top-2 -right-2 w-10 h-10 rounded-full flex items-center justify-center animate-bounce"
                style={{
                  background: "white",
                  boxShadow: "0 4px 12px rgba(255, 140, 97, 0.3)",
                  animationDuration: "2s",
                }}
              >
                <Heart
                  className="w-5 h-5 fill-current"
                  style={{ color: "var(--st-primary)" }}
                />
              </div>

              {/* Floating sparkle decoration */}
              <div
                className="absolute -bottom-2 -left-2 w-8 h-8 rounded-full flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, var(--st-gradient-primary-end) 0%, var(--st-gradient-primary-start) 100%)",
                  boxShadow: "0 4px 12px rgba(255, 140, 97, 0.3)",
                }}
              >
                <Sparkles className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrintComingSoonSection;
